import type { FC } from 'react';
import { Row, Column } from '@simplywallst/ui-core';
import type { AnalysisCheck } from '@/pages/CompanyReport/redux/interface';
import {
  ChecksTitle,
  CheckRow,
  CheckName,
  CheckArrow,
  IconFallback,
} from './styled';
import { useTheme } from '@simplywallst/ui-core';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import {
  VALUATION_PB_RATIO,
  VALUATION_PE_GROWTH,
  VALUATION_PE_RATIO,
  VALUATION_FAIR_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  ANALYST_PRICE_TARGETS,
  FUTURE_GROWTH,
  FUTURE_RETURN_EQUITY,
  PAST_EARNINGS_GROWTH,
  PAST_EARNINGS_REVENUE,
  PAST_ROE,
  HEALTH_DEBT_HISTORY,
  HEALTH_FINANCIAL_POSITION,
  DIVIDEND_YIELD,
  DIVIDEND_STABILITY_GROWTH,
  DIVIDEND_PAYOUT_CURRENT,
  DIVIDEND_PAYOUT_FUTURE,
  HEALTH_FINANCIAL_INSTITUTION,
  VALUATION_RATIO_VS_PEERS,
  VALUATION_RATIO_VS_INDUSTRY,
  HEALTH_CASH_RUNWAY,
} from '@/constants/anchors';
import { slugify } from '@/utilities/slugify';

const StatementPositiveSolid = loadable(
  () => import('@simplywallst/ui-core/icons'),
  {
    ssr: false,
    resolveComponent: (component) => component.StatementPositiveSolid,
    fallback: <IconFallback />,
  }
);
const StatementNegativeSolid = loadable(
  () => import('@simplywallst/ui-core/icons'),
  {
    ssr: false,
    resolveComponent: (component) => component.StatementNegativeSolid,
    fallback: <IconFallback />,
  }
);
interface Props {
  scoreName: string;
  checks: AnalysisCheck[];
  score: number;
}
export const StatementChecks: FC<Props> = ({ scoreName, checks, score }) => {
  const theme = useTheme();
  const tracking = useTracking();
  const scroller = useScrollIntoView();

  const mapHashToSection = (hash: string | undefined) => {
    switch (hash) {
      case 'isGoodValueComparingPreferredMultipleToPeersAverageValue':
        return VALUATION_RATIO_VS_PEERS;
      case 'isGoodValueComparingPreferredMultipleToIndustry':
        return VALUATION_RATIO_VS_INDUSTRY;
      case 'isGoodValueComparingRatioToFairRatio':
        return VALUATION_FAIR_RATIO;
      case 'isUndervaluedBasedOnDCF':
      case 'isHighlyUndervaluedBasedOnDCF':
        return VALUATION_PRICE_VS_FAIR_RATIO;
      case 'isAnalystForecastTrustworthy':
        return ANALYST_PRICE_TARGETS;
      case 'isUndervaluedOnPERelativeToPeers':
      case 'isUndervaluedOnPERelativeToMarket':
        return VALUATION_PE_RATIO;
      case 'isUndervaluedBasedOnPEG':
        return VALUATION_PE_GROWTH;
      case 'isUndervaluedBasedOnPB':
        return VALUATION_PB_RATIO;
      case 'isExpectedProfitGrowthAboveRiskFreeRate':
      case 'isExpectedAnnualProfitGrowthAboveMarket':
      case 'isExpectedAnnualProfitGrowthHigh':
      case 'isExpectedRevenueGrowthAboveMarket':
      case 'isExpectedRevenueGrowthHigh':
        return FUTURE_GROWTH;
      case 'isReturnOnEquityForecastAboveBenchmark':
        return FUTURE_RETURN_EQUITY;
      case 'hasHighQualityPastEarnings':
      case 'hasPastNetProfitMarginImprovedOverLastYear':
        return PAST_EARNINGS_REVENUE;
      case 'hasGrownProfitsOverPast5Years':
      case 'hasProfitGrowthAccelerated':
      case 'isGrowingFasterThanIndustry':
        return PAST_EARNINGS_GROWTH;
      case 'isReturnOnEquityAboveThreshold':
        return PAST_ROE;
      case 'areShortTermLiabilitiesCovered':
      case 'areLongTermLiabilitiesCovered':
        return HEALTH_FINANCIAL_POSITION;
      case 'isDebtLevelAppropriate':
      case 'hasDebtReducedOverTime':
      case 'isDebtCoveredByCashflow':
      case 'isInterestCoveredByProfit':
        return HEALTH_DEBT_HISTORY;
      case 'hasAnAppropriateLevelOfAssets':
      case 'hasAppropriateBadLoanAllowance':
      case 'hasPrimarilyLowRiskFunding':
      case 'hasAppropriateLoanLevel':
      case 'hasPrimarilyDepositFunding':
      case 'hasAppropriateNonPerformingLoans':
        return HEALTH_FINANCIAL_INSTITUTION;
      case 'hasCashRunwayIfStable':
      case 'hasCashRunwayIfGrowing':
        return HEALTH_CASH_RUNWAY;
      case 'isDividendSignificant':
      case 'isDividendYieldTopTier':
        return DIVIDEND_YIELD;
      case 'isDividendStable':
      case 'isDividendGrowing':
        return DIVIDEND_STABILITY_GROWTH;
      case 'isDividendCovered':
        return DIVIDEND_PAYOUT_CURRENT;
      case 'isDividendCoveredIn3Years':
      case 'isDividendCoveredByFreeCashFlow':
        return DIVIDEND_PAYOUT_FUTURE;

      default:
        return '';
    }
  };
  return (
    <Row alignItem="flex-end">
      <ChecksTitle data-cy-id={`${slugify(scoreName)}-score`}>
        {`${scoreName} Score`}
        <span>{` ${score}/${checks.length}`}</span>
      </ChecksTitle>
      <Column as="ul">
        {checks.map((check, i) => {
          const { title, hash } = check;
          const sectionHash = mapHashToSection(hash);
          return (
            <CheckRow
              as="li"
              data-cy-id={`statement-check-${check.hash}`}
              key={check.title + i}
              alignItem="center"
              onClick={() => {
                if (sectionHash) {
                  scroller(`#${sectionHash}`);
                  tracking.trackEvent({
                    action: 'click',
                    modifier: 'analysis_checks',
                    properties: hash,
                  });
                }
              }}
            >
              {check.mark === 'PASS' ? (
                <StatementPositiveSolid
                  fill={theme.color.good}
                  width={16}
                  data-cy-id="pass"
                />
              ) : (
                <StatementNegativeSolid
                  fill={theme.color.bad}
                  width={16}
                  data-cy-id="fail"
                />
              )}
              <CheckName>{title}</CheckName>
              <CheckArrow
                className="checkarrow"
                fill={theme.color.whiteRGBA(0.5)}
              />
            </CheckRow>
          );
        })}
      </Column>
    </Row>
  );
};
